/**
 * Группы пабликов в форме публикации (26.10.2026).
 *
 * Саяна выкладывает один ролик сразу в несколько пабликов — русские с одной
 * подписью, казахские с другой. Группа одним нажатием отмечает свои паблики,
 * «Как в прошлый раз» — последний набор. Здесь только чистая логика: что
 * отмечено, что получится после нажатия, что помнить между заходами.
 */

export interface AccountGroup {
  id: string;
  name: string;
  accountIds: string[];
}

export type GroupState = 'all' | 'some' | 'none';

/** Паблики группы, которые сейчас вообще можно отметить (доступны и активны). */
export function groupMembers(group: AccountGroup, available: ReadonlySet<string>): string[] {
  return group.accountIds.filter((id) => available.has(id));
}

export function groupState(group: AccountGroup, checked: ReadonlySet<string>, available: ReadonlySet<string>): GroupState {
  const members = groupMembers(group, available);
  if (!members.length) return 'none';
  const on = members.filter((id) => checked.has(id)).length;
  return on === members.length ? 'all' : on > 0 ? 'some' : 'none';
}

/**
 * Нажатие на группу: вся отмечена — снять её паблики, иначе — добавить
 * недостающие. Остальные отметки не трогаем: русские + казахские = обе группы.
 */
export function toggleGroup(group: AccountGroup, checked: ReadonlySet<string>, available: ReadonlySet<string>): Set<string> {
  const next = new Set(checked);
  const members = groupMembers(group, available);
  if (groupState(group, checked, available) === 'all') members.forEach((id) => next.delete(id));
  else members.forEach((id) => next.add(id));
  return next;
}

/** Отмеченные паблики группы — кому «Это же в группу» скопирует текст. */
export function checkedInGroup(group: AccountGroup, checked: ReadonlySet<string>): string[] {
  return group.accountIds.filter((id) => checked.has(id));
}

/** Новая группа из отмеченного; имя уже занято — заменяем её состав. */
export function upsertGroup(groups: readonly AccountGroup[], name: string, accountIds: Iterable<string>): AccountGroup[] {
  const clean = name.trim().slice(0, 40);
  const ids = [...new Set(accountIds)];
  const same = groups.find((g) => g.name.toLowerCase() === clean.toLowerCase());
  if (same) return groups.map((g) => (g === same ? { ...g, name: clean, accountIds: ids } : g));
  return [...groups, { id: newId(), name: clean, accountIds: ids }];
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }
}

/** Последний набор пабликов: свой у каждого человека и формата, только в этом браузере. */
const lastKey = (userId: string | null, format: string) => `publish:last-accounts:${userId ?? 'anon'}:${format}`;

export function loadLastAccounts(userId: string | null, format: string): string[] {
  try {
    const raw = localStorage.getItem(lastKey(userId, format));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function saveLastAccounts(userId: string | null, format: string, ids: Iterable<string>): void {
  const list = [...new Set(ids)];
  try {
    localStorage.setItem(lastKey(userId, format), JSON.stringify(list));
    // Тот же набор — и в общий «последний выбор» человека, без формата.
    localStorage.setItem(sharedKey(userId), JSON.stringify(list));
  } catch {
    // Приватное окно или запрет хранилища — просто не запомним.
  }
}

/**
 * Один «последний набор» на человека, а не на формат (02.11.2026): рилс и
 * пост уходят в одни и те же паблики, и после рилса форма поста не должна
 * встречать пустым списком. Старые ключи по формату читаем запасным путём —
 * у тех, кто публиковал до выкатки, набор не пропадает.
 */
const sharedKey = (userId: string | null) => `publish:last-accounts:${userId ?? 'anon'}`;

export function loadLastAccountSet(userId: string | null): string[] {
  const own = readIds(sharedKey(userId));
  if (own) return own;
  for (const format of ['reel', 'post']) {
    const old = readIds(lastKey(userId, format));
    if (old?.length) return old;
  }
  return [];
}

export function saveLastAccountSet(userId: string | null, ids: Iterable<string>): void {
  try {
    localStorage.setItem(sharedKey(userId), JSON.stringify([...new Set(ids)]));
  } catch {
    // Не запомним — не беда.
  }
}

/** null — ключа нет или в нём мусор; [] — набор сохранён пустым. */
function readIds(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

export function sameSet(a: ReadonlySet<string>, b: readonly string[]): boolean {
  return a.size === new Set(b).size && b.every((id) => a.has(id));
}

/**
 * Кому уходит правка текста, когда выбрано «Текст для: <группа>»: отмеченные
 * паблики группы. Только если паблик в превью — из них: иначе человек видит
 * один текст, а правил бы другой. null — обычное поведение формы.
 */
export function scopeTargets(
  groups: readonly AccountGroup[], scopeId: string | null, checked: ReadonlySet<string>, selectedId: string,
): string[] | null {
  const group = scopeId ? groups.find((g) => g.id === scopeId) : undefined;
  if (!group) return null;
  const ids = checkedInGroup(group, checked);
  return ids.includes(selectedId) ? ids : null;
}
